/**
 * Formatting for the Cycles screen (f10.2). Formatting only.
 *
 * Nothing here derives a cycle attribute: labels, closing months, month
 * windows, previous cycles and working-day dates all arrive from the API
 * (`derived`). These helpers only turn a server value into display text.
 */

import type { CycleDerived, CycleMilestone } from '@/api/client';

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** The column order of the list, keyed by the server's milestone keys. */
export const MILESTONE_COLUMNS: readonly { key: string; label: string }[] = [
  { key: 'PREPARE', label: 'Prepare' },
  { key: 'OPEN', label: 'Open' },
  { key: 'REVIEW', label: 'Review (actuals)' },
  { key: 'FREEZE', label: 'Freeze' },
  { key: 'SUBMIT', label: 'Submit' },
];

function shortMonth(month: number | undefined): string {
  return (MONTH_NAMES[(month ?? 1) - 1] ?? '').slice(0, 3);
}

/** `2026-08-05` -> `05 Aug 2026`, read as a calendar date (no time zone shift). */
export function formatCycleDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return `${day} ${shortMonth(Number(month))} ${year}`;
}

/** The milestone for a column, as the server sent it. */
export function milestoneFor(derived: CycleDerived, key: string): CycleMilestone | undefined {
  return derived.calendar.milestones.find((milestone) => milestone.key === key);
}

/** `WD3`, from the server's offset -- never hard-coded. */
export function workingDayTag(milestone: CycleMilestone | undefined): string {
  return milestone ? `WD${milestone.working_day}` : '';
}

/**
 * The header tag for a milestone column. The offsets are one Settings value
 * shared by every cycle, so any row carries it; an empty list shows none.
 */
export function columnTag(items: readonly { derived: CycleDerived }[], key: string): string {
  const first = items[0];
  return first ? workingDayTag(milestoneFor(first.derived, key)) : '';
}

/** `Sep 2026 – Dec 2026`, from the server's open months; null when none remain. */
export function openWindowText(derived: CycleDerived): string | null {
  const months = derived.open_months;
  if (!months.length) return null;
  const year = derived.closing_month.year;
  const first = `${shortMonth(months[0])} ${year}`;
  const last = `${shortMonth(months[months.length - 1])} ${year}`;
  return months.length === 1 ? first : `${first} – ${last}`;
}

/** `Jan 2026 – Jul 2026`, from the server's closed months. */
export function closedWindowText(derived: CycleDerived): string {
  const months = derived.closed_months;
  const year = derived.closing_month.year;
  const first = `${shortMonth(months[0])} ${year}`;
  return months.length === 1 ? first : `${first} – ${derived.closing_month.label}`;
}

/** The month before `today`'s, the dialog's default choice. */
export function defaultPeriod(today: Date): { year: number; month: number } {
  const month = today.getMonth(); // 0-based: already "the month before" in 1-based terms
  return month === 0
    ? { year: today.getFullYear() - 1, month: 12 }
    : { year: today.getFullYear(), month };
}
